import { queryKeyPrefix } from '@src/corelib/query/utils';
import { useMutation } from '@src/query';


/** Cache key prefix for a seller's eBay integrations. TanStack's default prefix
 * matching means invalidating this refreshes BOTH the connections list and the
 * `…/limits/` counts in one call. `sellerId` is used verbatim (no coercion) so
 * the key matches the connections/limits queries byte-for-byte. */
function integrationsKeyPrefix(sellerId) {
  return queryKeyPrefix('integrations', sellerId);
}


/**
 * M17 eBay integration mutation hooks (#293, Plan 2 Slice 4). Each wraps the
 * shared `useMutation` engine with the injected-`queryClient`
 * `mutateFn({ apiClient, queryClient })` contract — the corelib query singleton
 * is NOT imported here. Invalidation runs inside `mutateFn` (the one place
 * `queryClient` is injected). Both connection mutations invalidate the
 * integrations prefix `['root', '', 'integrations', sellerId]`, which by
 * TanStack's default prefix matching covers BOTH the connections list and the
 * `…/limits/` query (counts shift on disconnect) in a single call — the caller
 * passes that prefix as `queryKey`.
 *
 * Mirrors `query/settings/useChannelMutations.js`: one
 * `query/<area>/use<Thing>Mutations.js` module re-exported through the
 * `@src/query` barrel.
 */

/** Set a connection as the default for its type (clears the previous default).
 * Variables: `{ sellerId, connectionId, queryKey }`. */
function useIntegrationSetDefaultMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, sellerId, connectionId, queryKey }) => apiClient
      .request({
        url: `/integrations/${sellerId}/connections/${connectionId}/set-default/`,
        method: 'POST',
      })
      .then((data) => queryClient.invalidateQueries({ queryKey }).then(() => data)),
    ...params,
  });
}


/** Disconnect a connection (204; sets status=disconnected, clears tokens +
 * is_default). Variables: `{ sellerId, connectionId, queryKey }`. */
function useIntegrationDisconnectMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, sellerId, connectionId, queryKey }) => apiClient
      .request({
        url: `/integrations/${sellerId}/connections/${connectionId}/`,
        method: 'DELETE',
      })
      .then((data) => queryClient.invalidateQueries({ queryKey }).then(() => data)),
    ...params,
  });
}


/** Remove a connection (204; hard-deletes the row, unlike Disconnect which keeps
 * a disconnected row). Variables: `{ sellerId, connectionId, queryKey }`. */
function useIntegrationRemoveMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, sellerId, connectionId, queryKey }) => apiClient
      .request({
        url: `/integrations/${sellerId}/connections/${connectionId}/remove/`,
        method: 'DELETE',
      })
      .then((data) => queryClient.invalidateQueries({ queryKey }).then(() => data)),
    ...params,
  });
}


/** Rename a connection (Plan 3, Slice E): PATCH the user-set `label` so multiple
 * eBay accounts are distinguishable on the part Listings tab. Variables:
 * `{ sellerId, connectionId, label, queryKey }`. Invalidates the shared
 * integrations prefix (mirrors set-default) so the card + per-account listing
 * rows pick up the new name. */
function useIntegrationRenameMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, sellerId, connectionId, label, queryKey }) => apiClient
      .request({
        url: `/integrations/${sellerId}/connections/${connectionId}/`,
        method: 'PATCH',
        payload: { label },
      })
      .then((data) => queryClient.invalidateQueries({ queryKey }).then(() => data)),
    ...params,
  });
}


/** Run eBay business-policy onboarding for a connection (Plan 3, Slice C):
 * `POST …/connections/<id>/onboard/` opts the account into policy management and
 * create-or-selects the three business policies, returning the updated connection.
 * Variables: `{ sellerId, connectionId, queryKey }`. Invalidates the shared
 * integrations prefix (mirrors set-default) so the card's readiness summary
 * (derived from `business_policies`) refreshes. */
function useIntegrationOnboardMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, sellerId, connectionId, queryKey }) => apiClient
      .request({
        url: `/integrations/${sellerId}/connections/${connectionId}/onboard/`,
        method: 'POST',
      })
      .then((data) => queryClient.invalidateQueries({ queryKey }).then(() => data)),
    ...params,
  });
}


export {
  integrationsKeyPrefix,
  useIntegrationDisconnectMutation,
  useIntegrationOnboardMutation,
  useIntegrationRemoveMutation,
  useIntegrationRenameMutation,
  useIntegrationSetDefaultMutation,
};
